import React, { useState, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import "./styles/App.css";
import "./styles/noir-global.css";
import { ROUTES } from "./constants/routes";
import { ROLES } from "./constants/roles";
import "./styles/variables/colors.css";
import "./styles/variables/typography.css";
import "./styles/variables/identity.css";
import { FiMenu } from "react-icons/fi";
import { Toaster } from "react-hot-toast";

// ─── EAGER IMPORTS (critical for first paint) ───
import LandingPage from "./pages/auth/LandingPage/LandingPage.jsx";
import LoginPage from "./pages/auth/LoginPage/LoginPage.jsx";
import GoogleAuthCallback from "./pages/auth/GoogleAuthCallback/GoogleAuthCallback.jsx";
import Sidebar from "./components/layout/Sidebar/Sidebar.jsx";
import WalkthroughTour from "./components/common/WalkthroughTour/WalkthroughTour.jsx";
import ProtectedRoute from "./components/common/ProtectedRoute/ProtectedRoute.jsx";
import LoadingFallback from "./components/common/LoadingFallback/LoadingFallback.jsx";
import { ConstraintProvider } from "./context/ConstraintContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { ModalProvider } from "./context/ModalContext";
import { KeyboardShortcutsProvider } from "./pages/shared/InstructionsPage/InstructionsPage.jsx";
import ErrorBoundary from "./components/common/ErrorBoundary/ErrorBoundary.jsx";
import { ChatProvider } from "./context/ChatContext";


// ─── LAZY IMPORTS (code-split into separate chunks) ───
const lazyWithMinDuration = (importFunc, minDuration = 1800) => {
  return lazy(() =>
    Promise.all([
      importFunc(),
      new Promise((resolve) => setTimeout(resolve, minDuration)),
    ]).then(([moduleExports]) => moduleExports),
  );
};

// Shared pages
const Dashboard = lazyWithMinDuration(
  () => import("./pages/shared/Dashboard/Dashboard.jsx"),
);
const LeaveRequestsPage = lazyWithMinDuration(
  () => import("./pages/shared/LeaveRequestsPage/LeaveRequestsPage.jsx"),
);
const NotificationsPage = lazyWithMinDuration(
  () => import("./pages/shared/NotificationsPage/NotificationsPage.jsx"),
);
const PrivacyPolicy = lazyWithMinDuration(
  () => import("./pages/shared/PrivacyPolicy/PrivacyPolicy.jsx"),
);
const TermsOfService = lazyWithMinDuration(
  () => import("./pages/shared/TermsOfService/TermsOfService.jsx"),
);
const ChatPage = lazyWithMinDuration(
  () => import("./pages/shared/ChatPage/ChatPage.jsx"),
);
const InstructionsPage = lazyWithMinDuration(
  () => import("./pages/shared/InstructionsPage/InstructionsPage.jsx"),
);

// Admin pages
const TeachersPage = lazyWithMinDuration(
  () => import("./pages/admin/TeachersPage/TeachersPage.jsx"),
);
const StudentsPage = lazyWithMinDuration(
  () => import("./pages/admin/StudentsPage/StudentsPage.jsx"),
);
const SubjectsPage = lazyWithMinDuration(
  () => import("./pages/admin/SubjectsPage/SubjectsPage.jsx"),
);
const SectionsPage = lazyWithMinDuration(
  () => import("./pages/admin/SectionsPage/SectionsPage.jsx"),
);
const ConstraintsPage = lazyWithMinDuration(
  () => import("./pages/admin/ConstraintsPage/ConstraintsPage.jsx"),
);
const SmartTimetablePage = lazyWithMinDuration(
  () => import("./pages/admin/SmartTimetablePage/SmartTimetablePage.jsx"),
);
const LiveAttendancePage = lazyWithMinDuration(
  () => import("./pages/admin/LiveAttendancePage/LiveAttendancePage.jsx"),
);
const AdminAttendanceHistoryPage = lazyWithMinDuration(
  () => import("./pages/admin/AttendanceHistoryPage/AttendanceHistoryPage.jsx"),
);
const SavedTimetablePage = lazyWithMinDuration(
  () => import("./pages/admin/SavedTimetablePage/SavedTimetablePage.jsx"),
);
const AdminSettingsPage = lazyWithMinDuration(
  () => import("./pages/admin/AdminSettingsPage/AdminSettingsPage.jsx"),
);
const ReportsPage = lazyWithMinDuration(
  () => import("./pages/admin/ReportsPage/ReportsPage.jsx"),
);
const TimetableAdminPage = lazyWithMinDuration(
  () => import("./pages/admin/TimetableAdminPage/TimetableAdminPage.jsx"),
);
const AuditLogsPage = lazyWithMinDuration(
  () => import("./pages/admin/AuditLogsPage/AuditLogsPage.jsx"),
);
const ExamsPage = lazyWithMinDuration(
  () => import("./pages/admin/ExamsPage/ExamsPage.jsx"),
);
const ResourcesPage = lazyWithMinDuration(
  () => import("./pages/admin/ResourcesPage/ResourcesPage.jsx"),
);
const DisciplineLogsPage = lazyWithMinDuration(
  () => import("./pages/admin/DisciplineLogsPage/DisciplineLogsPage.jsx"),
);
const SystemConfigPage = lazyWithMinDuration(
  () => import("./pages/admin/SystemConfigPage/SystemConfigPage.jsx"),
);
const NotFoundPage = lazyWithMinDuration(
  () => import("./pages/shared/NotFoundPage/NotFoundPage.jsx"),
);

// Teacher pages
const TeacherTimetablePage = lazyWithMinDuration(
  () => import("./pages/teachers/TeacherTimetablePage/TeacherTimetablePage.jsx"),
);
const TeacherAttendanceHistoryPage = lazyWithMinDuration(
  () => import("./pages/teachers/AttendanceHistoryPage/AttendanceHistoryPage.jsx"),
);
const AssignmentsPage = lazyWithMinDuration(
  () => import("./pages/teachers/AssignmentsPage/AssignmentsPage.jsx"),
);
const GradesPage = lazyWithMinDuration(
  () => import("./pages/teachers/GradesPage/GradesPage.jsx"),
);
const TeacherSettingsPage = lazyWithMinDuration(
  () => import("./pages/teachers/TeacherSettingsPage/TeacherSettingsPage.jsx"),
);
const TeacherAttendancePage = lazyWithMinDuration(
  () => import("./pages/teachers/TeacherAttendancePage/TeacherAttendancePage.jsx"),
);

// IdentitySyncer — syncs the logged-in user's actual role to the identity context
// so the whole authenticated app reflects the correct role theme (admin=blue, teacher=violet).
const IdentitySyncer = ({ role }) => {
  const { switchIdentity } = useTheme();
  React.useEffect(() => {
    if (role) {
      const normalized = role.toLowerCase();
      if (normalized === ROLES.ADMIN.toLowerCase() || normalized === ROLES.TEACHER.toLowerCase()) {
        switchIdentity(normalized);
      }
    }
  }, [role, switchIdentity]);
  return null;
};


const LandingPageWrapper = () => {
  const navigate = useNavigate();
  return <LandingPage onNavigate={(view) => navigate(`/${view}`)} />;
};

// Helper component - wraps LoginPage to provide login handler
const LoginPageWrapper = ({ initialMode, onLogin }) => {
  return <LoginPage onLogin={onLogin} initialMode={initialMode} />;
};


const AppLayout = ({
  user,
  handleLogout,
  isSidebarOpen,
  onCloseSidebar,
  isCollapsed,
  toggleSidebarCollapse,
  openSidebar,
}) => (
  <div className="app-container">
    <Sidebar
      onLogout={handleLogout}
      userName={user?.name}
      userRole={user?.role || "teacher"}
      isOpen={isSidebarOpen}
      onClose={onCloseSidebar}
      isCollapsed={isCollapsed}
      toggleCollapse={toggleSidebarCollapse}
    />
    <div
      className={`app-main ${isCollapsed ? "app-main-collapsed" : ""}`}
      data-tour="main-content"
    >
      {/* Animated background */}
      <div className="jumbo"></div>

      <div className="mobile-topbar">
        <button
          type="button"
          className="mobile-menu-btn"
          onClick={openSidebar}
          aria-label="Open navigation menu"
        >
          <FiMenu aria-hidden="true" />
          <span>Menu</span>
        </button>
      </div>
      <Routes>
        <Route
          path={ROUTES.DASHBOARD}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.TEACHER]}>
              <Dashboard />
            </ProtectedRoute>
          }
        />

        {/* Admin Only Routes */}
        <Route
          path={ROUTES.TEACHERS}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <TeachersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENTS}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <StudentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SUBJECTS}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <SubjectsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SECTIONS}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <SectionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CONSTRAINTS}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <ConstraintsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TIMETABLE}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <SmartTimetablePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_TIMETABLE}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <TimetableAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.EXAMS}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <ExamsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.RESOURCES}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <ResourcesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.DISCIPLINE}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <DisciplineLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ATTENDANCE}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <LiveAttendancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.VIEW_TIMETABLE}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <SavedTimetablePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.REPORTS}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.AUDIT_LOGS}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <AuditLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SYSTEM_CONFIG}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <SystemConfigPage />
            </ProtectedRoute>
          }
        />

        {/* Teacher Only Routes */}
        <Route
          path={ROUTES.TEACHER_ATTENDANCE}
          element={
            <ProtectedRoute allowedRoles={[ROLES.TEACHER]}>
              <TeacherAttendancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.MY_TIMETABLE}
          element={
            <ProtectedRoute allowedRoles={[ROLES.TEACHER]}>
              <TeacherTimetablePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ASSIGNMENTS}
          element={
            <ProtectedRoute allowedRoles={[ROLES.TEACHER]}>
              <AssignmentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.GRADES}
          element={
            <ProtectedRoute allowedRoles={[ROLES.TEACHER]}>
              <GradesPage />
            </ProtectedRoute>
          }
        />

        {/* Shared Routes */}
        <Route
          path={ROUTES.ATTENDANCE_HISTORY}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.TEACHER]}>
              {user?.role?.toLowerCase() === "admin" ? (
                <AdminAttendanceHistoryPage />
              ) : (
                <TeacherAttendanceHistoryPage />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.LEAVE_REQUESTS}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.TEACHER]}>
              <LeaveRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.NOTIFICATIONS}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.TEACHER]}>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SETTINGS}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.TEACHER]}>
              {user?.role?.toLowerCase() === "admin" ? (
                <AdminSettingsPage user={user} onLogout={handleLogout} />
              ) : (
                <TeacherSettingsPage user={user} />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CHAT}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.TEACHER]}>
              <ChatPage />
            </ProtectedRoute>
          }
        />
        <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicy />} />
        <Route path={ROUTES.TERMS_OF_SERVICE} element={<TermsOfService />} />
        <Route
          path={ROUTES.INSTRUCTIONS}
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.TEACHER]}>
              <InstructionsPage />
            </ProtectedRoute>
          }
        />

        {/* Fallback for unknown protected routes */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
    <WalkthroughTour />
  </div>
);

const getStoredAdminUser = () => {
  // If we are impersonating, ignore the existing stored user to force a fresh session
  if (
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("token")
  ) {
    localStorage.removeItem("adminUser");
    return null;
  }
  const savedUser = localStorage.getItem("adminUser");
  if (!savedUser) return null;

  try {
    const parsedUser = JSON.parse(savedUser);
    if (!parsedUser?.token) {
      localStorage.removeItem("adminUser");
      return null;
    }
    return parsedUser;
  } catch (error) {
    localStorage.removeItem("adminUser");
    return null;
  }
};

// PublicRoute component - redirects authenticated users to dashboard
const PublicRoute = ({ children, isLoggedIn }) => {
  if (isLoggedIn) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }
  return children;
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return !!getStoredAdminUser();
  });

  const [user, setUser] = useState(() => getStoredAdminUser());

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleLogin = (userData) => {
    if (!userData?.token) {
      setUser(null);
      setIsLoggedIn(false);
      localStorage.removeItem("adminUser");
      return;
    }

    setUser(userData);
    setIsLoggedIn(true);
    localStorage.setItem("adminUser", JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    setIsLoggedIn(false);
    localStorage.removeItem("adminUser");
  };

  const openSidebar = () => setIsSidebarOpen(true);
  const closeSidebar = () => setIsSidebarOpen(false);
  const toggleSidebarCollapse = () => setIsCollapsed(!isCollapsed);

  return (
    <ThemeProvider>
      <IdentitySyncer role={user?.role} />
      <ModalProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: "var(--bg-primary, #1a1a2e)",
              color: "var(--text-primary, #e0e0e0)",
              border: "1px solid var(--border-color, #2a2a4a)",
              borderRadius: "12px",
              padding: "14px 20px",
              fontSize: "0.9rem",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
              zIndex: 9999999,
            },
            success: {
              iconTheme: { primary: "#22c55e", secondary: "#fff" },
            },
            error: {
              iconTheme: { primary: "#ef4444", secondary: "#fff" },
            },
          }}
          containerStyle={{ zIndex: 9999999 }}
        />
        <ConstraintProvider isLoggedIn={isLoggedIn}>
          <ErrorBoundary>
            <Router>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  {/* ─── PUBLIC ROUTES (with PublicRoute wrapper) ─── */}

                  <Route
                    path={ROUTES.HOME}
                    element={
                      <PublicRoute isLoggedIn={isLoggedIn}>
                        <LandingPageWrapper />
                      </PublicRoute>
                    }
                  />

                  <Route
                    path={ROUTES.LOGIN}
                    element={
                      <PublicRoute isLoggedIn={isLoggedIn}>
                        <LoginPageWrapper
                          initialMode="login"
                          onLogin={handleLogin}
                        />
                      </PublicRoute>
                    }
                  />

                  <Route
                    path={ROUTES.REGISTER}
                    element={
                      <PublicRoute isLoggedIn={isLoggedIn}>
                        <LoginPageWrapper
                          initialMode="register"
                          onLogin={handleLogin}
                        />
                      </PublicRoute>
                    }
                  />

                  {/* Google Auth Callback - special case */}
                  <Route
                    path="/auth/google/callback"
                    element={<GoogleAuthCallback onLogin={handleLogin} />}
                  />

                  {/* Policy Pages - always accessible */}
                  <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicy />} />
                  <Route
                    path={ROUTES.TERMS_OF_SERVICE}
                    element={<TermsOfService />}
                  />

                  {/* ─── PROTECTED APP LAYOUT & ROUTES ─── */}
                  {/* 
                    When NOT logged in, any path here (except catch-all) will be handled by the catch-all or filtered.
                    Redirect logic for protected routes is already inside ProtectedRoute.jsx.
                  */}
                  <Route
                    path="/*"
                    element={
                      isLoggedIn ? (
                        <KeyboardShortcutsProvider
                          isLoggedIn={isLoggedIn}
                          onLogout={handleLogout}
                          toggleSidebarCollapse={toggleSidebarCollapse}
                        >
                          <ChatProvider user={user}>
                            <AppLayout
                            user={user}
                            handleLogout={handleLogout}
                            isSidebarOpen={isSidebarOpen}
                            onCloseSidebar={closeSidebar}
                            isCollapsed={isCollapsed}
                            toggleSidebarCollapse={toggleSidebarCollapse}
                            openSidebar={openSidebar}
                          />
                          </ChatProvider>
                        </KeyboardShortcutsProvider>
                      ) : (
                        <Navigate to={ROUTES.LOGIN} replace />
                      )
                    }
                  />
                </Routes>
              </Suspense>
            </Router>
          </ErrorBoundary>
        </ConstraintProvider>
      </ModalProvider>
    </ThemeProvider>
  );
}

export default App;

